import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';

import { BASE_URI } from '../settings';
import { cancelFetchOnReentry } from '../core/util';
import {
  fetchJson,
  fetchResponse,
  isAbortError,
  pageToken,
  readJsonResponse,
  reportFetchError,
  SET_FETCH_ERROR,
  withRetryKey,
} from './FetchActions';

/*
 * Variants action types
 */
export const RECEIVE_VARIANT_TISSUE_PREDICTIONS =
  'RECEIVE_VARIANT_TISSUE_PREDICTIONS';

export const RECIEVE_VARIANTS = 'RECIEVE_VARIANTS';

export const RECEIVE_VARIANT_FEATURE_NAMES = 'RECEIVE_VARIANT_FEATURE_NAMES';

export const SET_VARIANT_ERRORS = 'SET_VARIANT_ERRORS';

/*
 * Variants action creators
 */

export function recieveVariants(variants) {
  return {
    type: RECIEVE_VARIANTS,
    variants,
  };
}

export function receiveTissueVariantPredictions(gene, predictions) {
  return {
    type: RECEIVE_VARIANT_TISSUE_PREDICTIONS,
    gene,
    predictions,
  };
}

export function receiveVariantFeatureNames(featureNames) {
  return {
    type: RECEIVE_VARIANT_FEATURE_NAMES,
    featureNames,
  };
}
export function setVariantErrors(errors) {
  return {
    type: SET_VARIANT_ERRORS,
    errors,
  };
}

// The newest search; an older one that settles later, whichever way, owns
// nothing. The re-entry adapter covers only the request, not the body read.
let latestVariantSearch;

export function fetchVariants(search) {
  return async (dispatch, getState, { fetch }) => {
    const attempt = {};
    latestVariantSearch = attempt;
    const current = () => latestVariantSearch === attempt;
    const page = pageToken();
    const report = withRetryKey(action => {
      if (action.type !== SET_FETCH_ERROR || current()) dispatch(action);
    }, 'variant-search');
    // The re-entry adapter swallows its own aborts; remember whether one
    // happened, because a superseded search is not a failure.
    let aborted = false;
    const observedFetch = async (...args) => {
      try {
        return await fetch(...args);
      } catch (error) {
        aborted = isAbortError(error);
        throw error;
      }
    };
    const response = await fetchResponse(
      cancelFetchOnReentry,
      report,
      observedFetch,
      search,
    );

    if (!current()) return;
    // Every real failure must land in the store, or the search keeps loading.
    if (!response) {
      if (!aborted) dispatch(setVariantErrors({ errors: 'unavailable' }));
      return;
    }
    if (!response.ok) {
      dispatch(setVariantErrors({ errors: response.statusText }));
      reportFetchError(report, response, undefined, page);
      return;
    }

    const json = await readJsonResponse(response, report, undefined, page);
    if (!current()) return;
    if (json === undefined) {
      dispatch(setVariantErrors({ errors: 'unreadable' }));
      return;
    }
    dispatch(recieveVariants(json));
  };
}

export function fetchVariantFeatureNames(featureLabel) {
  return async (dispatch, getstate, { fetch }) => {
    const featureNames = await fetchJson(
      fetch,
      dispatch,
      `${BASE_URI}variants/features/?label=${featureLabel}`,
    );
    if (featureNames === undefined) return;
    dispatch(receiveVariantFeatureNames(featureNames.map(f => f.name)));
  };
}

export function fetchTissueVariantPredictions(gene, database) {
  const featureParams = new URLSearchParams();
  if (database) featureParams.append('database', database);
  featureParams.append('collapse', 'true');
  const featureQueryString = featureParams.toString();

  return async (dispatch, getState, { fetch }) => {
    const predictions = await fetchJson(
      fetch,
      dispatch,
      `${BASE_URI}genes/${gene.entrez}/tissue_variants/?${featureQueryString}`,
    );
    if (predictions === undefined) {
      // An empty result is the page's terminal state; the banner says why.
      dispatch(
        receiveTissueVariantPredictions(gene.entrez, {
          variants: [],
          tissues: [],
        }),
      );
      return;
    }
    predictions.tissues.sort((t1, t2) => {
      if (t1.title < t2.title) return -1;
      if (t1.title > t2.title) return 1;
      return 0;
    });
    dispatch(receiveTissueVariantPredictions(gene.entrez, predictions));
  };
}
